/**
 * The options the print preview fits the route with.
 *
 * This exists to make sure a duration is always one of them. Mapbox's
 * `fitBounds` delegates to `flyTo` unless `linear` is set, and `flyTo` with
 * no duration derives one from the distance travelled — so the print page,
 * which opens at the user's last saved centre, spent 11.3 seconds swooping
 * across the Atlantic on load when that centre happened to be in California.
 *
 * 250ms matches `fitViewToSheet`, the page's other camera move, so both read
 * as the same gesture.
 */

/** Long enough to read as a movement rather than a cut, short enough to ignore. */
export const FIT_DURATION_MS = 250;

/** Keeps the route clear of the panel edge without framing it loosely. */
export const ROUTE_FIT_PADDING = 20;

/**
 * An upper bound on any caller-supplied duration. Nothing on this page has a
 * reason to animate for longer, and the cap is what stops a distance-derived
 * duration creeping back in.
 */
export const MAX_FIT_DURATION_MS = 2000;

export interface RouteFitOptions {
  padding: number;
  duration: number;
}

export function routeFitOptions(duration?: number | null): RouteFitOptions {
  return { padding: ROUTE_FIT_PADDING, duration: clampFitDuration(duration) };
}

/** The default for anything absent or nonsensical; 0 is honoured — it means "cut". */
export function clampFitDuration(duration?: number | null): number {
  if (typeof duration !== 'number' || !Number.isFinite(duration) || duration < 0) return FIT_DURATION_MS;

  return Math.min(duration, MAX_FIT_DURATION_MS);
}
